import apiClient from '../client';
import { ApiResponse, Participant, CreateParticipantDto, PaginationQuery, PaginationMeta } from '@/types';

export type MagicLinkSessionStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface MagicLinkStudent {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  school: { id: string; name: string } | null;
  sessionId: string | null;
  token: string | null;
  sessionStatus: MagicLinkSessionStatus | null;
  emailSentAt: string | null;
}

export interface MagicLinkStats {
  totalStudents: number;
  totalTokens: number;
  totalEmailed: number;
}

export interface MagicLinkStatusResponse {
  data: MagicLinkStudent[];
  meta: PaginationMeta;
  stats: MagicLinkStats;
}

export interface GenerateTokensResult {
  created: number;
  skipped: number;
}

export interface GenerateTokenForStudentResult {
  action: 'created' | 'regenerated';
  sessionId: number;
  token: string;
}

export interface SendAllResult {
  sent: number;
  skipped: number;
  failed: number;
  errors: string[];
}

export interface SendAllProgress {
  total: number;
  processed: number;
  sent: number;
  skipped: number;
  failed: number;
  currentEmail: string | null;
  percent: number;
}

/** Default bulk-send interval (ms) — must match backend EMAIL_SEND_INTERVAL_MS default. */
export const EMAIL_SEND_INTERVAL_MS = 2000;

export interface PromoteResult {
  promoted: number;
  skipped: number;
  notEligible: number;
}

export interface Stage1Completer {
  id: string;
  sessionId?: string;
  firstName: string;
  lastName: string;
  email: string;
  school: { id: string; name: string } | null;
  score: number | null;
  endedAt: string | null;
  durationFromStartClickMs: number | null;
  durationFromGlobalStartMs: number | null;
  term?: number;
  disqualified?: boolean;
  focusEventCount?: number;
}

export interface DemoteResult {
  deleted: number;
  studentId: string;
}

export interface AddToTerm2Result {
  added: number;
  alreadyExists: number;
  notFound: string[];
}

export interface ResetStageResult {
  deleted: number;
  stage: number;
  term?: number;
  reEnrolled?: number;
}

export const participantsApi = {
  getAll: async (
    params?: PaginationQuery & { schoolId?: string; search?: string; sessionStage?: number },
  ): Promise<{ data: Participant[]; meta: PaginationMeta }> => {
    const response = await apiClient.get<ApiResponse<Participant[]>>('/participants', { params });
    return { data: response.data.data ?? [], meta: response.data.meta! };
  },

  getById: async (id: string): Promise<Participant> => {
    const response = await apiClient.get<ApiResponse<Participant>>(`/participants/${id}`);
    return response.data.data!;
  },

  create: async (dto: CreateParticipantDto): Promise<Participant> => {
    const response = await apiClient.post<ApiResponse<Participant>>('/participants', dto);
    return response.data.data!;
  },

  update: async (id: string, dto: Partial<CreateParticipantDto>): Promise<Participant> => {
    const response = await apiClient.patch<ApiResponse<Participant>>(`/participants/${id}`, dto);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/participants/${id}`);
  },

  getStage2QualifiedIds: async (): Promise<string[]> => {
    const response = await apiClient.get<ApiResponse<string[]>>('/participants/stage2-qualified-ids');
    return response.data.data ?? [];
  },

  getMagicLinkStatus: async (
    params?: PaginationQuery & { search?: string; stage?: number; term?: number },
  ): Promise<MagicLinkStatusResponse> => {
    const response = await apiClient.get<ApiResponse<MagicLinkStudent[]> & { stats: MagicLinkStats }>(
      '/participants/magic-links',
      { params },
    );
    return {
      data: response.data.data ?? [],
      meta: response.data.meta!,
      stats: response.data.stats,
    };
  },

  generateTokens: async (stage = 1, term = 1): Promise<GenerateTokensResult> => {
    const response = await apiClient.post<ApiResponse<GenerateTokensResult>>(
      '/participants/tokens',
      { stage, term },
    );
    return response.data.data!;
  },

  generateTokenForStudent: async (
    studentId: string,
    stage = 1,
    term = 1,
  ): Promise<GenerateTokenForStudentResult> => {
    const response = await apiClient.post<ApiResponse<GenerateTokenForStudentResult>>(
      `/participants/${studentId}/tokens`,
      { stage, term },
    );
    return response.data.data!;
  },

  sendMagicLink: async (
    studentId: string,
    stage = 1,
    term = 1,
  ): Promise<{ studentId: string; email: string; sentAt: string }> => {
    const response = await apiClient.post<ApiResponse<{ studentId: string; email: string; sentAt: string }>>(
      `/participants/${studentId}/send-email`,
      undefined,
      { params: { stage, term } },
    );
    return response.data.data!;
  },

  sendAllMagicLinks: async (stage = 1, term = 1): Promise<SendAllResult> => {
    const response = await apiClient.post<ApiResponse<SendAllResult>>(
      '/participants/send-all-emails',
      { stage, term },
    );
    return response.data.data!;
  },

  sendAllMagicLinksStream: async (
    stage: number,
    term: number,
    onProgress: (progress: SendAllProgress) => void,
    signal?: AbortSignal,
  ): Promise<SendAllResult> => {
    const baseUrl = import.meta.env.VITE_API_URL || '/api/v1';
    const response = await fetch(`${baseUrl}/participants/send-all-emails/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({ stage, term }),
      signal,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Brak odpowiedzi ze streamu');

    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult: SendAllResult | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() ?? '';

      for (const chunk of chunks) {
        if (!chunk.trim()) continue;

        let eventName = 'message';
        let dataLine = '';
        for (const line of chunk.split('\n')) {
          if (line.startsWith('event: ')) eventName = line.slice(7).trim();
          else if (line.startsWith('data: ')) dataLine = line.slice(6);
        }
        if (!dataLine) continue;

        const parsed = JSON.parse(dataLine) as SendAllProgress | SendAllResult | { message: string };
        if (eventName === 'progress') onProgress(parsed as SendAllProgress);
        else if (eventName === 'done') finalResult = parsed as SendAllResult;
        else if (eventName === 'error') throw new Error((parsed as { message: string }).message);
      }
    }

    if (!finalResult) throw new Error('Stream zakończył się bez wyniku');
    return finalResult;
  },

  promoteToStage2: async (studentIds: string[]): Promise<PromoteResult> => {
    const response = await apiClient.post<ApiResponse<PromoteResult>>(
      '/participants/promote-to-stage2',
      { studentIds: studentIds.map(Number) },
    );
    return response.data.data!;
  },

  autoPromoteTopN: async (topN: number): Promise<PromoteResult & { topN: number; selected: Stage1Completer[]; cleared: number }> => {
    const response = await apiClient.post<ApiResponse<PromoteResult & { topN: number; selected: Stage1Completer[]; cleared: number }>>(
      '/participants/auto-promote',
      { topN },
    );
    return response.data.data!;
  },

  addStudentsToTerm2: async (emails: string[]): Promise<AddToTerm2Result> => {
    const response = await apiClient.post<ApiResponse<AddToTerm2Result>>(
      '/participants/add-to-term2',
      { emails },
    );
    return response.data.data!;
  },

  demoteFromStage2: async (studentId: string): Promise<DemoteResult> => {
    const response = await apiClient.post<ApiResponse<DemoteResult>>(
      `/participants/${studentId}/demote-stage2`,
    );
    return response.data.data!;
  },

  resetStageForTesting: async (stage: number, term?: number): Promise<ResetStageResult> => {
    const response = await apiClient.post<ApiResponse<ResetStageResult>>(
      '/participants/reset-stage',
      { stage, ...(term !== undefined ? { term } : {}) },
    );
    return response.data.data!;
  },
};
